import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { BACKEND } from "../config/configEnvs";
import Modal from "../components/ui/Modal";
import GetWorkerType from "../utils/helpers/GetWorkerType";
import { FaPlus, FaSearch, FaPaperPlane, FaArrowLeft, FaUserCircle, FaBuilding, FaCog, FaCalendarPlus, FaEdit, FaCalendar, FaClock, FaTag } from "react-icons/fa";
import InputFormValue from "../components/ui/components/InputFormValue";
import Multiselector from "../components/ui/components/Multiselector";
import { FaTicket } from "react-icons/fa6";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "react-quill/dist/quill.bubble.css";
import {
  QUILL_FORMATS,
  QUILL_MODULES_EDIT,
  QUILL_MODULES_READONLY,
  normalizeToDelta,
  quillValueToPlainText,
  toQuillDisplayValue,
} from "../utils/quillUtils";

const PRIORITIES = ["Sin definir", "Bajo", "Medio", "Alto"];
const STATUSES = ["Abierto", "Cerrado"];
const TICKET_TYPES = [
  { label: "Soporte", value: "Soporte" },
  { label: "Quejas y reclamos", value: "Quejas y reclamos" },
  { label: "Comercial", value: "Comercial" },
];

const getId = (rel) => {
  if (!rel) return null;
  if (typeof rel === "string") return rel;
  if (typeof rel === "object" && rel?.id) return rel.id;
  // Payload relationship-to-many-collections (polymorphic) can be { relationTo, value }
  if (typeof rel === "object" && rel?.value) {
    if (typeof rel.value === "string") return rel.value;
    if (typeof rel.value === "object" && rel.value?.id) return rel.value.id;
  }
  return null;
};

const getLabel = (rel) => {
  if (!rel) return "-";
  if (typeof rel === "string") return rel;
  if (typeof rel === "object") {
    return (
      rel?.nombreComercial ||
      rel?.name ||
      rel?.title ||
      rel?.email ||
      rel?.id ||
      "-"
    );
  }
  return "-";
};

const formatDayTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-PE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-PE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return "";
  }
};

const formatTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("es-PE", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const Tickets = () => {
  const user = useSelector((state) => state.auth.user);

  const [limit] = useState(5);

  // Left pane (inbox)
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [clientFilterId, setClientFilterId] = useState("");
  const [typeFilter, setTypeFilter] = useState([]);

  // Right pane (chat)
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messageDraft, setMessageDraft] = useState("");
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Create ticket modal
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createTitle, setCreateTitle] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [createPriority, setCreatePriority] = useState("Sin definir");
  const [createStatus, setCreateStatus] = useState("Abierto");
  const [createClientId, setCreateClientId] = useState("");
  const [createService, setCreateService] = useState(null);
  const [servicesForClient, setServicesForClient] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // Clientes for autocomplete (shared for filter + create)
  const [clientsOptions, setClientsOptions] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  const selectedTicketId = useMemo(() => getId(selectedTicket?.id || selectedTicket), [selectedTicket]);
  
  const selectedTypes = useMemo(() => {
    if (!selectedTicket?.type) return [];
    // type puede ser un array o un string
    if (Array.isArray(selectedTicket.type)) {
      return selectedTicket.type;
    }
    return [selectedTicket.type];
  }, [selectedTicket]);

  // Verificar si el usuario es administración
  const isAdmin = useMemo(() => {
    return GetWorkerType(user) === "Administrativo";
  }, [user]);

  // Obtener tipos disponibles según el tipo de trabajador
  const availableTypes = useMemo(() => {
    const workerType = GetWorkerType(user);
    if (workerType === "Administrativo" || workerType === "Comercial") {
      return TICKET_TYPES; // Ve todo
    }
    // Operativa solo ve "Quejas y reclamos"
    return TICKET_TYPES.filter((t) => t.value === "Quejas y reclamos");
  }, [user]);

  const fetchClientesUser = useCallback(async () => {
    try {
      setLoadingClients(true);
      const params = new URLSearchParams();
      params.set("limit", "0");
      params.set("depth", "0");
      // sort is optional depending on backend; safe to omit if unsupported
      // params.set("sort", "nombreComercial");

      const res = await axios.get(`${BACKEND}/api/clientesUser?${params.toString()}`);
      const docs = res?.data?.docs || [];
      setClientsOptions(
        docs.map((c) => ({
          value: c.id,
          label: c?.nombreComercial || c?.commercialName || c?.name || c?.email || c?.id,
        }))
      );
    } catch (e) {
      console.log(e);
      toast.error("Error obteniendo clientes");
    } finally {
      setLoadingClients(false);
    }
  }, []);

  const scrollToTop = useCallback(() => {
    requestAnimationFrame(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = 0;
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      setLoadingTickets(true);

      const params = new URLSearchParams();
      params.set("limit", limit);
      params.set("depth", "2");
      params.set("sort", "-updatedAt");

      // Construir filtros de búsqueda de texto (OR)
      const q = search?.trim();
      let searchOrIndex = 0;
      if (q) {
        params.set("where[or][0][title][like]", q);
        params.set("where[or][1][description][like]", q);
        searchOrIndex = 2;
      }
      
      if (statusFilter) params.set("where[status][equals]", statusFilter);
      if (priorityFilter) params.set("where[priority][equals]", priorityFilter);
      if (clientFilterId) params.set("where[client][equals]", clientFilterId);
      
      // Filtrar solo tickets asignados al trabajador actual
      const workerId = user?.id;
      if (workerId) {
        params.set("where[workersAssigned][contains]", workerId);
      }

      // Filtrar por tipo según el área del usuario
      const userArea = user?.area?.toUpperCase();
      const isAdmin = userArea === "ADMINISTRACION";
      const isCommercial = userArea === "COMERCIAL";
      const isOperative = !isAdmin && !isCommercial;
      
      // Filtro de tipo: primero el filtro manual, luego el filtro por área
      if (typeFilter && typeFilter.length > 0) {
        // Si hay filtro manual de tipos, usar OR para múltiples tipos
        // Continuar desde donde terminó la búsqueda de texto
        typeFilter.forEach((type, index) => {
          params.set(`where[or][${searchOrIndex + index}][type][contains]`, type);
        });
      } else {
        // Si no hay filtro manual, aplicar filtro por área
        if (isOperative) {
          params.set("where[type][contains]", "Soporte");
        }
      }
      // Admin y Comercial ven todo (no se aplica filtro)

      const res = await axios.get(`${BACKEND}/api/tickets?${params.toString()}`);
      const docs = res?.data?.docs || [];
      setTickets(docs);

      // Keep selection stable if possible
      if (selectedTicketId) {
        const still = docs.find((t) => t.id === selectedTicketId);
        if (still) setSelectedTicket(still);
      }
    } catch (e) {
      console.log(e);
      toast.error("Error obteniendo tickets");
    } finally {
      setLoadingTickets(false);
    }
  }, [search, statusFilter, priorityFilter, clientFilterId, typeFilter, selectedTicketId, limit, user?.id]);

  const fetchMessages = useCallback(async (ticketId) => {
    if (!ticketId) {
      setMessages([]);
      return;
    }
    try {
      setLoadingMessages(true);
      const params = new URLSearchParams();
      params.set("limit", "0");
      params.set("depth", "2");
      params.set("sort", "-createdAt");
      params.set("where[ticket][equals]", ticketId);

      const res = await axios.get(`${BACKEND}/api/ticketMessages?${params.toString()}`);
      setMessages(res?.data?.docs || []);
      scrollToTop();
    } catch (e) {
      console.log(e);
      toast.error("Error obteniendo mensajes del ticket");
    } finally {
      setLoadingMessages(false);
    }
  }, [scrollToTop]);

  const fetchServicesForClient = useCallback(async (clientId) => {
    if (!clientId) {
      setServicesForClient([]);
      return;
    }
    try {
      setLoadingServices(true);
      const params = new URLSearchParams();
      params.set("limit", "0");
      params.set("depth", "2");
      params.set("sort", "-createdAt");
      params.set("where[cotizacion.cliente][equals]", clientId);

      const res = await axios.get(`${BACKEND}/api/serviciosCotizaciones?${params.toString()}`);
      setServicesForClient(res?.data?.docs || []);
    } catch (e) {
      console.log(e);
      toast.error("Error obteniendo servicios del cliente");
    } finally {
      setLoadingServices(false);
    }
  }, []);

  const createTicket = useCallback(async () => {
    const title = createTitle.trim();
    const clientId = createClientId;
    const serviceId = getId(createService);
    const descPlain = quillValueToPlainText(createDesc);
    if (!title) return toast.error("El título es obligatorio");
    if (!descPlain) return toast.error("Debes agregar una descripción");
    if (descPlain.length > 1000) return toast.error("La descripción no puede superar 1000 caracteres");
    if (!clientId) return toast.error("Selecciona un cliente");
    if (!serviceId) return toast.error("Selecciona un servicio");

    try {
      setCreating(true);
      const payload = {
        title,
        // Backend expects Quill Delta (same approach as ConfirmsModal)
        description: normalizeToDelta(createDesc),
        client: clientId,
        service: serviceId,
        priority: createPriority,
        status: createStatus,
      };

      const res = await axios.post(`${BACKEND}/api/tickets`, payload);
      const created = res?.data?.doc || res?.data;
      toast.success("Ticket creado");
      setOpenCreate(false);

      // reset create form (keep priority/status defaults)
      setCreateTitle("");
      setCreateDesc("");
      setCreateClientId("");
      setCreateService(null);
      setServicesForClient([]);

      await fetchTickets();
      if (created?.id) {
        setSelectedTicket(created);
        await fetchMessages(created.id);
      }
    } catch (e) {
      console.log(e);
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.errors?.[0]?.message ||
        e?.message ||
        "Error creando ticket";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  }, [createTitle, createDesc, createClientId, createService, createPriority, createStatus, fetchTickets, fetchMessages]);

  const updateTicketField = useCallback(async (ticketId, patch) => {
    if (!ticketId) return;
    try {
      const res = await axios.put(`${BACKEND}/api/tickets/${ticketId}`, patch);
      const updated = res?.data?.doc || res?.data;
      setSelectedTicket(updated);
      setTickets((prev) => prev.map((t) => (t.id === ticketId ? updated : t)));
    } catch (e) {
      console.log(e);
      toast.error("Error actualizando ticket");
    }
  }, []);

  const sendMessage = useCallback(async () => {
    const ticketId = selectedTicketId;
    const html = messageDraft || "";
    const plain = quillValueToPlainText(html);
    if (!ticketId) return;
    if (!plain || plain === "") return;

    try {
      const senderId = user?.id;
      if (!senderId) {
        toast.error("No se pudo identificar tu usuario");
        return;
      }

      await axios.post(`${BACKEND}/api/ticketMessages`, {
        ticket: ticketId,
        // Payload polymorphic relationship expects { relationTo, value }
        sender: { relationTo: "trabajadores", value: senderId }, 
        message: normalizeToDelta(html),
      });

      setMessageDraft("");
      await fetchMessages(ticketId);
      scrollToTop();

      await fetchTickets();
    } catch (e) {
      console.log(e);
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.errors?.[0]?.message ||
        e?.message ||
        "Error enviando mensaje";
      toast.error(msg);
    }
  }, [selectedTicketId, messageDraft, user, fetchMessages, selectedTicket?.status, updateTicketField, fetchTickets, scrollToTop]);

  // initial + when filters change
  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // load clients once for autocompletes
  useEffect(() => {
    fetchClientesUser();
  }, [fetchClientesUser]);

  // when ticket selection changes
  useEffect(() => {
    if (!selectedTicketId) {
      setMessages([]);
      return;
    }
    fetchMessages(selectedTicketId);
  }, [selectedTicketId, fetchMessages]);

  // when create client changes, fetch services and clear service selection
  useEffect(() => {
    const clientId = createClientId;
    setCreateService(null);
    if (clientId) fetchServicesForClient(clientId);
    else setServicesForClient([]);
  }, [createClientId, fetchServicesForClient]);

  const ticketPreview = useMemo(() => {
    return tickets.map((t) => {
      const clientName = getLabel(t.client);
      const serviceName = getLabel(t.service?.servicio || t.service?.servicio?.name || t.service?.servicio?.id || t.service);
      return {
        ...t,
        _clientName: clientName,
        _serviceName:
          (typeof t?.service === "object" && t?.service?.servicio?.name) ||
          (typeof t?.service === "object" && t?.service?.servicio?.title) ||
          (typeof t?.service === "object" && t?.service?.servicio?.id) ||
          (typeof t?.service === "object" && t?.service?.id) ||
          serviceName,
      };
    });
  }, [tickets]);

  const renderSenderName = (sender) => {
    const value = sender?.value;
    const obj = typeof value === "object" ? value : sender;
    return getLabel(obj);
  };

  const renderSenderRole = (sender) => {
    if (sender?.relationTo === "clientesUser") return "Cliente";
    if (sender?.relationTo === "trabajadores") return "Operador";
    return sender?.relationTo || "System";
  };

  const isClientSender = (sender) => sender?.relationTo === "clientesUser";
  const isCurrentUserSender = (sender) => {
    if (!user?.id) return false;
    const senderId = getId(sender?.value || sender);
    return sender?.relationTo === "trabajadores" && senderId === user.id;
  };

  const getSenderPhoto = (sender) => {
    if (sender?.relationTo === "trabajadores") {
      const worker = sender?.value || sender;
      if (typeof worker === "object" && worker?.foto?.url) {
        return worker.foto.url;
      }
    }
    return null;
  };

  const getClientInitial = (sender) => {
    if (sender?.relationTo === "clientesUser") {
      const client = sender?.value || sender;
      if (typeof client === "object") {
        const nombreComercial = client?.nombreComercial || client?.commercialName || client?.name || "";
        return nombreComercial.charAt(0).toUpperCase() || "C";
      }
    }
    return "C";
  };

  const getAvatarBgColor = (sender) => {
    if (sender?.relationTo === "clientesUser") {
      return "bg-blue-500";
    }
    if (isCurrentUserSender(sender)) {
      return "bg-green-500";
    }
    return "bg-gray-500";
  };

  return (
    <div className="flex flex-col h-screen w-full">
      <Modal
        activeModal={openCreate}
        centered
        title={"Nuevo ticket"}
        className="max-w-5xl"
        onClose={() => setOpenCreate(false)}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-gray-700">Título *</label>
            <input
              value={createTitle}
              onChange={(e) => setCreateTitle(e.target.value)}
              className="text-xs rounded-lg border border-gray-300 px-3 py-2"
              placeholder="Ej: Error en formulario / Falta acceso / Cambio solicitado…"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-gray-700">Descripción</label>
            <div className="rounded-lg border border-gray-300 overflow-hidden">
              <div className="[&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-gray-200 [&_.ql-container]:border-0 [&_.ql-editor]:text-xs [&_.ql-editor]:min-h-[120px]">
                <ReactQuill
                  theme="bubble"
                  value={createDesc}
                  onChange={setCreateDesc}
                  modules={QUILL_MODULES_EDIT}
                  formats={QUILL_FORMATS}
                  placeholder="Agrega contexto, pasos para reproducir, links, etc."
                />
              </div>
            </div>
            <div className="text-[11px] text-gray-400">
              {Math.min(quillValueToPlainText(createDesc).length, 1000)}/1000
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-gray-700">Prioridad</label>
              <select
                value={createPriority}
                onChange={(e) => setCreatePriority(e.target.value)}
                className="text-xs rounded-lg border border-gray-300 px-3 py-2 bg-white"
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-gray-700">Estado</label>
              <select
                value={createStatus}
                onChange={(e) => setCreateStatus(e.target.value)}
                className="text-xs rounded-lg border border-gray-300 px-3 py-2 bg-white"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <InputFormValue
              title="Cliente"
              required
              icon={<FaSearch />}
              value={createClientId}
              setValue={setCreateClientId}
              type="autocomplete"
              minChars={0}
              options={clientsOptions}
              placeholder={loadingClients ? "Cargando clientes..." : "Buscar cliente..."}
              onSelectOption={(opt) => {
                setCreateClientId(opt?.value || "");
              }}
              disabled={loadingClients}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-gray-700">Servicio/Cotización *</label>
            <select
              value={getId(createService) || ""}
              onChange={(e) => {
                const id = e.target.value;
                const found = servicesForClient.find((s) => s.id === id);
                setCreateService(found || null);
              }}
              disabled={!createClientId || loadingServices}
              className="text-xs rounded-lg border border-gray-300 px-3 py-2 bg-white disabled:bg-gray-100"
            >
              <option value="">
                {!createClientId ? "Selecciona un cliente primero" : loadingServices ? "Cargando..." : "Selecciona un servicio"}
              </option>
              {servicesForClient.map((svc) => (
                <option key={svc.id} value={svc.id}>
                  {svc?.servicio?.name || svc?.servicio?.title || svc?.servicio?.id || svc.id} — {svc?.cotizacion?.id || "sin cotización"}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setOpenCreate(false)}
              className="text-xs px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={createTicket}
              disabled={creating}
              className="text-xs px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
            >
              {creating ? "Creando..." : "Crear ticket"}
            </button>
          </div>
        </div>
      </Modal>

      {/* NavBar */}
      <div className="flex flex-row px-4 py-2 bg-gray-100">
        <div className="flex flex-row w-full items-center gap-4 justify-between overflow-x-auto overflow-y-hidden px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex items-center gap-4 py-4">
            <FaTicket className="text-zinc-700 text-2xl" />
            <h2 className="text-2xl font-bold text-zinc-700">Tickets</h2>
          </div>
          <div className="flex w-full items-center justify-between gap-3">
            <button
              onClick={() => setOpenCreate(true)}
              className="text-xs px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
            >
              <FaPlus /> Nuevo
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
        {/* Left inbox */}
        <div
          className={`w-full md:w-[380px] border-r border-gray-200 bg-white flex flex-col ${
            selectedTicketId ? "hidden md:flex" : ""
          }`}
        >
          <div className="p-3 border-b border-gray-200">
            <div className="flex gap-2 items-center">
              <div className="relative flex-1">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="text-xs w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300"
                  placeholder="Buscar por título o descripción…"
                />
              </div>
              <button
                onClick={fetchTickets}
                className="text-xs px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Buscar
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="text-xs rounded-lg border border-gray-300 px-2 py-2 bg-white"
              >
                <option value="">Estado (todos)</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="text-xs rounded-lg border border-gray-300 px-2 py-2 bg-white"
              >
                <option value="">Prioridad (todas)</option>
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div className="mt-2">
              <Multiselector
                title="Tipo"
                allValue="Todos"
                disabled={false}
                value={typeFilter}
                handlerChange={(selected) => setTypeFilter(selected)}
                options={TICKET_TYPES}
                formControlHeight={32}
                formControlWidth="100%"
                menuItemWitdh={200}
                itemHeight={48}
                menuItemHeight="30px"
                itemWidth="100%"
              />
            </div>

            <div className="mt-3">
              <InputFormValue
                title="Cliente"
                icon={<FaSearch />}
                value={clientFilterId}
                setValue={setClientFilterId}
                type="autocomplete"
                minChars={0}
                options={clientsOptions}
                placeholder={loadingClients ? "Cargando clientes..." : "Filtrar por cliente..."}
                onSelectOption={(opt) => setClientFilterId(opt?.value || "")}
                disabled={loadingClients}
              />
            </div>
          </div>

          <div className="flex-1 overflow-auto">
            {loadingTickets ? (
              <div className="p-4 text-xs text-gray-500">Cargando tickets…</div>
            ) : ticketPreview.length === 0 ? (
              <div className="p-4 text-xs text-gray-500">No hay tickets con estos filtros.</div>
            ) : (
              ticketPreview.map((t) => {
                const active = selectedTicketId && t.id === selectedTicketId;
                const status = t.status || "Abierto";
                const priority = t.priority || "Bajo";
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedTicket(t)}
                    className={`w-full text-left px-3 py-3 border-b border-gray-100 hover:bg-gray-50 ${active ? "bg-blue-50" : "bg-white"}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {t.title}
                        </div>
                        <div className="text-xs text-gray-600 truncate">
                          {t._clientName} · {t._serviceName}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-[10px] px-2 py-1 whitespace-nowrap rounded-full ${status === "Cerrado" ? "bg-gray-200 text-gray-700" : "bg-green-100 text-green-700"}`}>
                          {status}
                        </span>
                        <span className={`text-[10px] px-2 py-1 whitespace-nowrap rounded-full ${priority === "Alto" ? "bg-red-100 text-red-700" : priority === "Medio" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>
                          {priority}
                        </span>
                      </div>
                    </div>
            {t.description ? (
              <div className="text-xs text-gray-500 mt-2 line-clamp-2">
                {quillValueToPlainText(t.description)}
              </div>
            ) : null}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Ticket view */}
        <div className={`flex-1 flex flex-col bg-gray-50 ${!selectedTicketId ? "hidden md:flex" : ""}`}>
          {!selectedTicketId ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
              Selecciona un ticket para ver el detalle.
            </div>
          ) : (
            <>
              {/* Top Bar (title + actions) */}
              <div className="px-4 py-3 bg-white border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0 flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => setSelectedTicket(null)}
                    className="sm:hidden h-9 w-9 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center"
                    title="Volver"
                  >
                    <FaArrowLeft className="text-gray-700" />
                  </button>
                  <div className="hidden sm:flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-gray-700">
                    <FaTicket />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">{selectedTicket?.title}</div>
                    <div className="text-[11px] text-gray-500 truncate">Ticket ID: {selectedTicketId}</div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 justify-start sm:justify-end">
                  {isAdmin && (
                    <div className="flex flex-wrap gap-2">
                      {availableTypes.map((type) => {
                        const isSelected = selectedTypes.includes(type.value);
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => {
                              let newTypes;
                              if (isSelected) {
                                // No permitir remover si es el último
                                if (selectedTypes.length > 1) {
                                  newTypes = selectedTypes.filter((t) => t !== type.value);
                                } else {
                                  return; // No hacer nada si es el último
                                }
                              } else {
                                newTypes = [...selectedTypes, type.value];
                              }
                              updateTicketField(selectedTicketId, { type: newTypes });
                            }}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors font-medium ${
                              isSelected
                                ? "bg-blue-100 text-blue-700 border-blue-300 shadow-sm"
                                : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                            }`}
                            title={isSelected && selectedTypes.length === 1 ? "Debe haber al menos un tipo seleccionado" : ""}
                          >
                            {type.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {!isAdmin && selectedTypes.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedTypes.map((typeValue) => {
                        const type = TICKET_TYPES.find((t) => t.value === typeValue);
                        if (!type) return null;
                        return (
                          <span
                            key={type.value}
                            className="text-xs px-3 py-1.5 rounded-full border bg-blue-100 text-blue-700 border-blue-300 font-medium"
                          >
                            {type.label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {isAdmin ? (
                    <select
                      value={selectedTicket?.status || "Abierto"}
                      onChange={(e) => updateTicketField(selectedTicketId, { status: e.target.value })}
                      className="text-xs rounded-lg border border-gray-300 px-2 py-2 bg-white"
                      title="Estado"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-xs px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-600">
                      {selectedTicket?.status || "Abierto"}
                    </div>
                  )}
                  <select
                    value={selectedTicket?.priority || "Sin definir"}
                    onChange={(e) => updateTicketField(selectedTicketId, { priority: e.target.value })}
                    className="text-xs rounded-lg border border-gray-300 px-2 py-2 bg-white"
                    title="Prioridad"
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => fetchMessages(selectedTicketId)}
                    className="text-xs px-3 py-2 rounded-lg border border-orange-500 hover:bg-orange-600 hover:text-white bg-orange-500 text-white font-bold transition-all duration-100"
                  >
                    Actualizar
                  </button>
                </div>
              </div>

              {selectedTicket?.status === "Cerrado" ? (
                <div className="mx-4 mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-700">
                  Este ticket está cerrado. Puedes responder para reabrirlo.
                </div>
              ) : null}

              <div className="flex-1 overflow-auto p-4">
                <div className="flex flex-col gap-4">

                  {/* Detalles */}
                  <div className="bg-white/30 backdrop-blur-lg rounded-lg border border-gray-200/30 sticky top-0 z-10 shadow-lg">
                    <div className="px-4 py-3 border-b border-gray-200/30 flex items-center justify-between gap-3 bg-white/40 backdrop-blur-md rounded-t-lg">
                      <div className="text-sm font-semibold text-gray-900">Detalles</div>
                      {selectedTypes.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                            <FaTag className="text-gray-400" />
                            <span>Tipo:</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedTypes.map((typeValue) => {
                              const type = TICKET_TYPES.find((t) => t.value === typeValue);
                              if (!type) return null;
                              return (
                                <span
                                  key={type.value}
                                  className="text-xs px-3 py-1.5 rounded-full border bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700 font-medium shadow-sm"
                                >
                                  {type.label}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="px-4 py-3 bg-white/20 backdrop-blur-md">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1">
                            <FaBuilding className="text-gray-400" />
                            <span>Cliente</span>
                          </div>
                          <div className="text-xs font-semibold text-gray-900 truncate">{getLabel(selectedTicket?.client)}</div>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1">
                            <FaCog className="text-gray-400" />
                            <span>Servicio</span>
                          </div>
                          <div className="text-xs font-medium text-gray-900 truncate">{getLabel(selectedTicket?.service?.servicio || selectedTicket?.service)}</div>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1">
                            <FaCalendarPlus className="text-gray-400" />
                            <span>Fecha de creación</span>
                          </div>
                          <div className="text-xs font-medium text-gray-900 truncate">{formatDayTime(selectedTicket?.createdAt)}</div>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1">
                            <FaEdit className="text-gray-400" />
                            <span>Última actualización</span>
                          </div>
                          <div className="text-xs font-medium text-gray-900 truncate">{formatDayTime(selectedTicket?.updatedAt)}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Reply */}
                  <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Responder</div>
                    </div>
                    <div className="px-4 py-3">
                      <div className="rounded-lg overflow-hidden border border-slate-300 dark:border-slate-600 bg-white [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-slate-200 [&_.ql-container]:border-0 [&_.ql-editor]:text-xs [&_.ql-editor]:min-h-[8rem]">
                        <ReactQuill
                          theme="snow"
                          value={messageDraft || ""}
                          onChange={(content, delta, source, editor) => {
                            setMessageDraft(editor.getContents());
                          }}
                          modules={QUILL_MODULES_EDIT}
                          formats={QUILL_FORMATS}
                          placeholder="Escribe tu respuesta…"
                        />
                      </div>
                      <div className="flex justify-end pt-3">
                        <button
                          onClick={sendMessage}
                          disabled={!messageDraft || quillValueToPlainText(messageDraft).length === 0}
                          className="text-xs px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                          title="Enviar (Ctrl+Enter)"
                        >
                          <FaPaperPlane /> Enviar respuesta
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Conversación */}
                  <div className="bg-white rounded-lg border border-gray-200">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <div className="text-sm font-semibold text-gray-900">Conversación</div>
                    </div>

                    <div className="p-4">
                      {loadingMessages ? (
                        <div className="text-xs text-gray-500">Cargando mensajes…</div>
                      ) : (
                        <div className="flex flex-col gap-4">
                          {messages.map((m) => {
                            const fromClient = isClientSender(m.sender);
                            const mine = isCurrentUserSender(m.sender);
                            const accent = fromClient ? "border-l-4 border-l-gray-300" : mine ? "border-l-4 border-l-blue-600" : "border-l-4 border-l-gray-900";
                            return (
                              <div key={m.id} className={`rounded-lg border border-gray-200 bg-white ${accent}`}>
                                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
                                  <div className="flex items-center gap-3 min-w-0 flex-1">
                                    {fromClient ? (
                                      <div className={`w-6 h-6 rounded-full ${getAvatarBgColor(m.sender)} flex items-center justify-center text-white font-semibold text-[10px] flex-shrink-0`}>
                                        {getClientInitial(m.sender)}
                                      </div>
                                    ) : getSenderPhoto(m.sender) ? (
                                      <img 
                                        src={getSenderPhoto(m.sender)} 
                                        alt={renderSenderName(m.sender)} 
                                        className="w-6 h-6 rounded-full object-cover flex-shrink-0" 
                                      />
                                    ) : (
                                      <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                        <FaUserCircle className="w-4 h-4 text-gray-400" />
                                      </div>
                                    )}
                                    <div className="min-w-0">
                                      <div className="text-sm font-semibold text-gray-900 truncate">
                                        {renderSenderName(m.sender)}
                                      </div>
                                      <div className="text-[11px] text-gray-500">
                                        {renderSenderRole(m.sender)}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                                    <div className="flex items-center gap-1 text-[11px] font-bold text-gray-700 whitespace-nowrap">
                                      <FaCalendar className="text-[10px]" />
                                      <span>{formatDate(m.createdAt)}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-[11px] font-bold text-gray-500 whitespace-nowrap">
                                      <FaClock className="text-[10px]" />
                                      <span>{formatTime(m.createdAt)}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="px-4 py-3">
                                  <div className="[&_.ql-container]:border-0 [&_.ql-editor]:p-0 [&_.ql-editor]:text-xs [&_.ql-editor]:text-gray-900 [&_.ql-editor]:leading-5">
                                    <ReactQuill
                                      theme="bubble"
                                      value={toQuillDisplayValue(m.message)}
                                      readOnly
                                      modules={QUILL_MODULES_READONLY}
                                      formats={QUILL_FORMATS}
                                      rows={4}
                                    />
                                  </div>
                                </div>
                                {(m.attachments?.length || 0) > 0 ? (
                                  <div className="px-4 pb-3 text-[11px] text-gray-500">
                                    Adjuntos: {m.attachments.length}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                          
                          {/* Descripción inicial del ticket como primer mensaje */}
                          {selectedTicket?.description && (
                            <div className="rounded-lg border border-gray-200 bg-white border-l-4 border-l-gray-300">
                              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  {selectedTicket?.client ? (
                                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-[10px] flex-shrink-0">
                                      {getClientInitial({ relationTo: "clientesUser", value: selectedTicket.client })}
                                    </div>
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                      <FaUserCircle className="w-4 h-4 text-gray-400" />
                                    </div>
                                  )}
                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold text-gray-900 truncate">
                                      {getLabel(selectedTicket?.client) || "Cliente"}
                                    </div>
                                    <div className="text-[11px] text-gray-500">
                                      Cliente
                                    </div>
                                  </div>
                                </div>
                                  <span className="text-[10px] px-2 py-1 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
                                    Mensaje original
                                  </span>
                                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                  <div className="flex flex-col items-end gap-0.5">
                                    <div className="flex items-center gap-1 text-[11px] font-bold text-gray-700 whitespace-nowrap">
                                      <FaCalendar className="text-[10px]" />
                                      <span>{formatDate(selectedTicket?.createdAt)}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-[11px] font-bold text-gray-500 whitespace-nowrap">
                                      <FaClock className="text-[10px]" />
                                      <span>{formatTime(selectedTicket?.createdAt)}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="px-4 py-3">
                                <div className="[&_.ql-container]:border-0 [&_.ql-editor]:p-0 [&_.ql-editor]:text-xs [&_.ql-editor]:text-gray-900 [&_.ql-editor]:leading-5">
                                  <ReactQuill
                                    theme="bubble"
                                    value={toQuillDisplayValue(selectedTicket.description)}
                                    readOnly
                                    modules={QUILL_MODULES_READONLY}
                                    formats={QUILL_FORMATS}
                                  />
                                </div>

                              </div>
                            </div>
                          )}
                          
                          <div ref={messagesEndRef} />
                        </div>
                      )}
                    </div>
                  </div>
                  
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tickets;